import { ApiError, type ApiClient } from '../../api/client';
import type {
  WorkflowDelegation,
  WorkflowDelegationInput,
  WorkflowMaintenanceResult,
  WorkflowOperationsPort,
  WorkflowPolicy,
  WorkflowPolicyRole,
  WorkflowPolicyUpdateInput,
} from '../../features/workflow/public';

type CollectionResponse<T> = {
  data: T[];
};

function mutationKey(value: string | undefined): string {
  return value ?? crypto.randomUUID();
}

export function createWorkflowOperationsHttpAdapter(client: ApiClient): WorkflowOperationsPort {
  return {
    async listPolicies() {
      const response = await client.requestTenant<CollectionResponse<WorkflowPolicy>>(
        '/api/hr/v1/{tenant}/workflowPolicies',
      );
      return response.data;
    },

    async listPolicyRoles() {
      const response = await client.requestTenant<CollectionResponse<WorkflowPolicyRole>>(
        '/api/hr/v1/{tenant}/workflowPolicyRoles',
      );
      return response.data;
    },

    updatePolicy(policyId: string, input: WorkflowPolicyUpdateInput) {
      return client.requestTenant<WorkflowPolicy>(
        `/api/hr/v1/{tenant}/workflowPolicies/${encodeURIComponent(policyId)}`,
        { method: 'PUT', body: JSON.stringify(input) },
      );
    },

    async listDelegations(active = true) {
      const response = await client.requestTenant<CollectionResponse<WorkflowDelegation>>(
        `/api/hr/v1/{tenant}/delegations?active=${active}`,
      );
      return response.data;
    },

    createDelegation(input: WorkflowDelegationInput, idempotencyKey?: string) {
      return client.requestTenant<WorkflowDelegation>('/api/hr/v1/{tenant}/delegations', {
        method: 'POST',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
        body: JSON.stringify(input),
      });
    },

    revokeDelegation(delegationId: string) {
      return client.requestTenant<WorkflowDelegation>(
        `/api/hr/v1/{tenant}/delegations/${encodeURIComponent(delegationId)}/revoke`,
        { method: 'POST' },
      );
    },

    runMaintenance() {
      return client.requestTenant<WorkflowMaintenanceResult>(
        '/api/hr/v1/{tenant}/workflowMaintenance/run',
        { method: 'POST', body: '{}' },
      );
    },

    isConflict(reason: unknown) {
      return reason instanceof ApiError && reason.status === 409;
    },

    isNotFound(reason: unknown) {
      return reason instanceof ApiError && reason.status === 404;
    },
  };
}
